import React from 'react';
import type { OptimizationOpportunity } from './ParameterDiscovery';

interface OptimizationOpportunityCellProps {
    opportunity?: OptimizationOpportunity;
    className?: string;
}

const OptimizationOpportunityCell: React.FC<OptimizationOpportunityCellProps> = ({ opportunity, className = '' }) => {
    if (!opportunity) {
        return <span className="text-zinc-500">—</span>;
    }

    // Determine color based on tier
    let colorClass = 'text-zinc-400';
    let bgClass = '';
    let tierLabel = '';
    let tierIcon = '';

    switch (opportunity.tier) {
        case 'high':
            colorClass = 'text-green-400 font-bold';
            bgClass = 'bg-green-900/20';
            tierLabel = 'High';
            tierIcon = '⬆';
            break;
        case 'moderate':
            colorClass = 'text-yellow-400 font-semibold';
            bgClass = 'bg-yellow-900/20';
            tierLabel = 'Moderate';
            tierIcon = '➡';
            break;
        case 'low':
            colorClass = 'text-zinc-400';
            tierLabel = 'Low';
            tierIcon = '⬇';
            break;
        case 'none':
            return <span className="text-zinc-600">None</span>;
    }

    const title = [
        `Optimization Opportunity: ${opportunity.score.toFixed(0)}/100`,
        `Strong Positive: ${opportunity.strongPositiveGroups} groups`,
        `Strong Negative: ${opportunity.strongNegativeGroups} groups`,
        `Potential Improvement: ${opportunity.potentialImprovement.toFixed(1)}pp`,
        `Coverage: ${(opportunity.actionableCoverage * 100).toFixed(0)}%`,
        opportunity.recommendation ? `Recommendation: ${opportunity.recommendation}` : ''
    ]
        .filter(Boolean)
        .join('\n');

    const ariaLabel = `Optimization opportunity: ${tierLabel} tier, score ${opportunity.score.toFixed(0)} out of 100`;

    return (
        <div
            aria-label={ariaLabel}
            className={`flex items-center gap-1 px-1 py-0.5 rounded ${bgClass} ${className}`}
            role="status"
            title={title}
        >
            <span aria-hidden="true" className={`${colorClass} text-sm`}>
                {opportunity.score.toFixed(0)}
            </span>
            <span aria-hidden="true" className="text-zinc-500 text-xs">
                <span className="sr-only">{tierLabel}</span>
                <span aria-hidden="true">{tierIcon}</span> {tierLabel.slice(0, 3)}
            </span>
        </div>
    );
};

export default React.memo(OptimizationOpportunityCell);
